// Members — each member's capital account by year, and the forms that move it:
// contributions (cash, property, an expense a member paid) and distributions.
import { useEffect, useState } from 'react';
import { useBooksAccess } from '../../contexts/AuthContext';
import { Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { day, memberWords, pct } from '../../lib/format';
import { journal, members as membersApi, settings } from '../../lib/ledger/api';
import { MEMBERS, type Account, type JournalEntry, type Member, type MemberCapital, type MemberSettings } from '../../lib/ledger/types';
import { AccountSelect, Money, MoneyInput, ReadOnlyNote, today, useAccounts } from './shared';

const DEFAULT_NAMES: MemberSettings = { leah: { name: 'Leah' }, laura: { name: 'Laura Means' } };
const DEFAULT_SHARES: Record<Member, number> = { leah: 51, laura: 49 };

type Kind = 'cash' | 'property' | 'expense_paid';
const KIND_WORDS: Record<Kind, string> = { cash: 'Cash', property: 'Property', expense_paid: 'Expense a member paid' };
const CATEGORIES: { value: 'equipment' | 'furniture' | 'software' | 'other'; label: string }[] = [
  { value: 'equipment', label: 'Equipment' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'software', label: 'Software' },
  { value: 'other', label: 'Other' },
];

export function MembersTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();
  const [names, setNames] = useState<MemberSettings>(DEFAULT_NAMES);
  const [shares, setShares] = useState<Record<Member, number>>(DEFAULT_SHARES);
  const [year, setYear] = useState(new Date().getFullYear());
  const [cap, setCap] = useState<MemberCapital | null>(null);
  const [capErr, setCapErr] = useState<unknown>(null);
  const [capLoading, setCapLoading] = useState(true);
  const [contribs, setContribs] = useState<JournalEntry[] | null>(null);
  const [dists, setDists] = useState<JournalEntry[] | null>(null);
  const [listErr, setListErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([settings.all(), settings.shares()]).then(([s, sh]) => {
      if (!alive) return;
      if (s.status === 'fulfilled') {
        const m = s.value.members as MemberSettings | undefined;
        if (m?.leah?.name && m?.laura?.name) setNames(m);
      }
      if (sh.status === 'fulfilled' && sh.value) setShares(sh.value);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setCapLoading(true);
    setCapErr(null);
    membersApi.capital(year)
      .then((c) => alive && setCap(c))
      .catch((e) => { if (alive) { setCapErr(e); setCap(null); } })
      .finally(() => alive && setCapLoading(false));
    const from = `${year}-01-01`;
    const to = `${year}-12-31`;
    setListErr(null);
    Promise.allSettled([
      journal.page({ from, to, source: 'contribution', limit: 500 }),
      journal.page({ from, to, source: 'distribution', limit: 500 }),
    ]).then(([c, d]) => {
      if (!alive) return;
      if (c.status === 'fulfilled') setContribs(c.value?.entries ?? []); else { setContribs([]); setListErr(c.reason); }
      if (d.status === 'fulfilled') setDists(d.value?.entries ?? []); else { setDists([]); setListErr(d.reason); }
    });
    return () => { alive = false; };
  }, [year, tick]);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const nameOf = (m: Member) => names[m]?.name ?? memberWords(m);

  function saved(label: string) {
    setDone(label);
    setTick((t) => t + 1);
  }

  return (
    <>
      <p className="lp-sub" style={{ margin: '0 0 12px' }}>
        Rootegra LLC has two members: {nameOf('leah')} ({pct(shares.leah)}) and {nameOf('laura')} ({pct(shares.laura)}).
        Income is split by those percentages; the capital accounts carry what each member has put in, earned, and taken out.
      </p>
      {!canEdit && <ReadOnlyNote />}
      {done && <Note tone="good">{done}</Note>}

      <Section
        title={`Capital accounts, ${year}`}
        right={
          <label className="lp-row lp-no-print">
            <span className="lp-field-label">Year</span>
            <select className="lp-sel" style={{ width: 'auto' }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        }
      >
        <ErrorNote error={capErr} notConfigured="The ledger is not set up yet." />
        {capLoading && !cap && <Empty>Loading…</Empty>}
        {cap && (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead>
                <tr>
                  <th>Member</th>
                  <th className="num">Beginning</th>
                  <th className="num">Contributions</th>
                  <th className="num">Share of income</th>
                  <th className="num">Distributions</th>
                  <th className="num">Ending</th>
                </tr>
              </thead>
              <tbody>
                {MEMBERS.map((m) => {
                  const r = cap.members?.[m];
                  return (
                    <tr key={m}>
                      <td><strong>{nameOf(m)}</strong> <span className="muted">· {pct(r?.pct ?? shares[m])}</span></td>
                      <td className="num"><Money cents={r?.beginning_cents} /></td>
                      <td className="num"><Money cents={r?.contributions_cents} /></td>
                      <td className="num"><Money cents={r?.allocated_income_cents} /></td>
                      <td className="num"><Money cents={r ? -Math.abs(r.distributions_cents) : 0} /></td>
                      <td className="num"><strong><Money cents={r?.ending_cents} /></strong></td>
                    </tr>
                  );
                })}
                <tr className="total">
                  <td>Net income {year}</td>
                  <td></td><td></td>
                  <td className="num"><Money cents={cap.net_income_cents} /></td>
                  <td></td><td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <p className="lp-field-hint" style={{ marginTop: 10 }}>
          Beginning is the balance on January 1. Share of income is net income times the member's percentage; {nameOf('leah')}'s share is rounded and {nameOf('laura')} takes the cent.
        </p>
      </Section>

      <Section title="Record a contribution">
        <ContributionForm accounts={chart.accounts} names={names} disabled={!canEdit} onSaved={saved} />
      </Section>

      <Section title="Record a distribution">
        <DistributionForm accounts={chart.accounts} names={names} disabled={!canEdit} onSaved={saved} />
      </Section>

      <Section title={`Contributions in ${year}`}>
        <ErrorNote error={listErr} />
        <EntryList entries={contribs} names={names} empty={`No contributions recorded in ${year}.`} kindFrom="details" />
      </Section>

      <Section title={`Distributions in ${year}`}>
        <EntryList entries={dists} names={names} empty={`No distributions recorded in ${year}.`} kindFrom="ref" />
      </Section>
    </>
  );
}

function EntryList({ entries, names, empty, kindFrom }: { entries: JournalEntry[] | null; names: MemberSettings; empty: string; kindFrom: 'details' | 'ref' }) {
  if (entries === null) return <Empty>Loading…</Empty>;
  if (entries.length === 0) return <Empty>{empty}</Empty>;
  return (
    <div className="lp-tbl-wrap">
      <table className="lp-tbl">
        <thead>
          <tr>
            <th>Date</th>
            <th>Member</th>
            <th>Memo</th>
            <th>{kindFrom === 'details' ? 'Kind' : 'Reference'}</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const member = e.lines?.find((l) => l.member)?.member ?? null;
            const total = (e.lines ?? []).reduce((s, l) => s + Number(l.debit_cents ?? 0), 0);
            const rawKind = e.details?.kind;
            const kind = kindFrom === 'details'
              ? (rawKind ? KIND_WORDS[rawKind as Kind] ?? String(rawKind) : '—')
              : String(e.details?.method ?? e.details?.reference ?? e.source_ref ?? '—');
            return (
              <tr key={e.id} className={e.is_reversed ? 'quiet' : ''}>
                <td style={{ whiteSpace: 'nowrap' }}>{day(e.entry_date)}</td>
                <td>{member ? names[member]?.name ?? memberWords(member) : '—'}</td>
                <td>{e.memo || '—'}{e.is_reversed && <> <span className="lp-tag lp-tag--warn">Reversed</span></>}</td>
                <td>{kind}</td>
                <td className="num"><Money cents={total} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ContributionForm({ accounts, names, disabled, onSaved }: { accounts: Account[]; names: MemberSettings; disabled: boolean; onSaved: (label: string) => void }) {
  const [member, setMember] = useState<Member>('leah');
  const [date, setDate] = useState(today());
  const [kind, setKind] = useState<Kind>('cash');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [taxBasis, setTaxBasis] = useState<number | null>(null);
  const [depositAccount, setDepositAccount] = useState('');
  const [expenseAccount, setExpenseAccount] = useState('');
  const [assetName, setAssetName] = useState('');
  const [category, setCategory] = useState<'equipment' | 'furniture' | 'software' | 'other'>('equipment');
  const [inService, setInService] = useState(today());
  const [life, setLife] = useState('60');
  const [salvage, setSalvage] = useState<number | null>(null);
  const [consent, setConsent] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const lifeMonths = Number(life);
  const ready =
    !disabled && !busy && Boolean(date) && description.trim().length > 0 && (amount ?? 0) > 0 &&
    (kind === 'cash' ? Boolean(depositAccount) : kind === 'expense_paid' ? Boolean(expenseAccount) : (assetName.trim().length > 0 && Boolean(inService) && Number.isInteger(lifeMonths) && lifeMonths > 0));

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await membersApi.contribute({
        member, date, kind, description: description.trim(), amount_cents: amount ?? 0,
        tax_basis_cents: kind === 'property' ? taxBasis : null,
        deposit_account_id: kind === 'cash' ? depositAccount : null,
        expense_account_id: kind === 'expense_paid' ? expenseAccount : null,
        asset: kind === 'property' ? {
          name: assetName.trim(), description: description.trim(), category,
          acquired_on: date, in_service_on: inService, cost_cents: amount ?? 0,
          tax_basis_cents: taxBasis, salvage_cents: salvage ?? 0, life_months: lifeMonths,
          source: 'contribution', contributed_by: member, consent_ref: consent.trim() || null,
        } : null,
        consent_ref: consent.trim() || null,
      });
      onSaved(`Contribution from ${names[member]?.name ?? memberWords(member)} recorded.`);
      setDescription(''); setAmount(null); setTaxBasis(null); setAssetName(''); setSalvage(null); setConsent('');
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lp-form" style={{ maxWidth: 760 }}>
      <div className="lp-form-grid">
        <Field label="Member">
          <select className="lp-sel" value={member} disabled={disabled} onChange={(e) => setMember(e.target.value as Member)}>
            {MEMBERS.map((m) => <option key={m} value={m}>{names[m]?.name ?? memberWords(m)}</option>)}
          </select>
        </Field>
        <Field label="Date"><input className="lp-inp" type="date" value={date} disabled={disabled} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Kind">
          <select className="lp-sel" value={kind} disabled={disabled} onChange={(e) => setKind(e.target.value as Kind)}>
            {(Object.keys(KIND_WORDS) as Kind[]).map((k) => <option key={k} value={k}>{KIND_WORDS[k]}</option>)}
          </select>
        </Field>
        <Field label={kind === 'property' ? 'Amount (fair market value)' : 'Amount'}>
          <MoneyInput cents={amount} onChange={setAmount} disabled={disabled} />
        </Field>
      </div>
      <Field label="Description" hint={kind === 'property' ? 'What it is and where it came from.' : 'What this is for, in a sentence.'}>
        <input className="lp-inp" value={description} disabled={disabled} onChange={(e) => setDescription(e.target.value)} />
      </Field>

      {kind === 'cash' && (
        <Field label="Deposited to" hint="The cash or bank account the money landed in.">
          <AccountSelect value={depositAccount} onChange={setDepositAccount} accounts={accounts} types={['asset']} disabled={disabled} />
        </Field>
      )}
      {kind === 'expense_paid' && (
        <Field label="Expense account" hint="What the member paid for on Rootegra's behalf.">
          <AccountSelect value={expenseAccount} onChange={setExpenseAccount} accounts={accounts} types={['expense', 'cogs']} disabled={disabled} />
        </Field>
      )}
      {kind === 'property' && (
        <div className="lp-form-grid">
          <Field label="Asset name"><input className="lp-inp" value={assetName} disabled={disabled} onChange={(e) => setAssetName(e.target.value)} /></Field>
          <Field label="Category">
            <select className="lp-sel" value={category} disabled={disabled} onChange={(e) => setCategory(e.target.value as typeof category)}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="In service from" hint="Depreciation starts this month."><input className="lp-inp" type="date" value={inService} disabled={disabled} onChange={(e) => setInService(e.target.value)} /></Field>
          <Field label="Life in months" hint="60 for equipment is the usual."><input className="lp-inp" inputMode="numeric" value={life} disabled={disabled} onChange={(e) => setLife(e.target.value)} /></Field>
          <Field label="Salvage value" hint="What it will be worth at the end. Usually zero."><MoneyInput cents={salvage} onChange={setSalvage} disabled={disabled} /></Field>
          <Field label="Tax basis (optional)" hint="What the member paid for it, or less."><MoneyInput cents={taxBasis} onChange={setTaxBasis} disabled={disabled} /></Field>
        </div>
      )}
      <Field label="Consent reference" hint='Where the other member said yes, for example "Leah email 2026-10-16".'>
        <input className="lp-inp" value={consent} disabled={disabled} onChange={(e) => setConsent(e.target.value)} />
      </Field>

      <ErrorNote error={err} />
      <div className="lp-row">
        <button type="button" className="lp-btn lp-btn--primary" disabled={!ready} onClick={() => void submit()}>{busy ? 'Saving…' : 'Record the contribution'}</button>
      </div>

      <div className="lp-field-hint" style={{ marginTop: 14, lineHeight: 1.5 }}>
        <p style={{ margin: '0 0 6px' }}>Book value is the fair market value on the day it comes in. Tax basis is what the member paid for it, or less, and goes to the accountant.</p>
        <p style={{ margin: 0 }}>A contribution needs the other member's written OK. It does not change the {pct(51)} / {pct(49)} split unless the operating agreement says so.</p>
      </div>
    </div>
  );
}

function DistributionForm({ accounts, names, disabled, onSaved }: { accounts: Account[]; names: MemberSettings; disabled: boolean; onSaved: (label: string) => void }) {
  const [member, setMember] = useState<Member>('leah');
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState<number | null>(null);
  const [fromAccount, setFromAccount] = useState('');
  const [method, setMethod] = useState('Mercury');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const ready = !disabled && !busy && Boolean(date) && (amount ?? 0) > 0 && Boolean(fromAccount);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await membersApi.distribute({ member, date, amount_cents: amount ?? 0, from_account_id: fromAccount, method, reference: reference.trim() });
      onSaved(`Distribution to ${names[member]?.name ?? memberWords(member)} recorded.`);
      setAmount(null); setReference('');
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lp-form" style={{ maxWidth: 760 }}>
      <div className="lp-form-grid">
        <Field label="Member">
          <select className="lp-sel" value={member} disabled={disabled} onChange={(e) => setMember(e.target.value as Member)}>
            {MEMBERS.map((m) => <option key={m} value={m}>{names[m]?.name ?? memberWords(m)}</option>)}
          </select>
        </Field>
        <Field label="Date"><input className="lp-inp" type="date" value={date} disabled={disabled} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Amount"><MoneyInput cents={amount} onChange={setAmount} disabled={disabled} /></Field>
        <Field label="Paid from" hint="The cash or bank account the money left.">
          <AccountSelect value={fromAccount} onChange={setFromAccount} accounts={accounts} types={['asset']} disabled={disabled} />
        </Field>
        <Field label="How">
          <select className="lp-sel" value={method} disabled={disabled} onChange={(e) => setMethod(e.target.value)}>
            <option>Mercury</option>
            <option>Check</option>
            <option>Zelle</option>
            <option>Retained</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Reference" hint="Check number, transfer id, or a note."><input className="lp-inp" value={reference} disabled={disabled} onChange={(e) => setReference(e.target.value)} /></Field>
      </div>
      <ErrorNote error={err} />
      <div className="lp-row">
        <button type="button" className="lp-btn lp-btn--primary" disabled={!ready} onClick={() => void submit()}>{busy ? 'Saving…' : 'Record the distribution'}</button>
      </div>
      <p className="lp-field-hint" style={{ marginTop: 10 }}>
        Payouts from the Apple tab post here on their own. Use this form for anything paid outside that waterfall.
      </p>
    </div>
  );
}

